import { create } from "zustand";
import tradeDataStore from "@/store/tradeDataStore";

// ─── Constants ──────────────────────────────────────────────────────────────

const NOTIFICATION_STORAGE_KEY = "notificationHistory";
export const MAX_NOTIFICATIONS = 100;
export const MAX_AGE_MS = 2 * 24 * 60 * 60 * 1000; // 2 days

// ─── Types ──────────────────────────────────────────────────────────────────

export interface NotificationMessage {
  id: string;
  message: string;
  timestamp: Date;
  type: string;
  tradeId: string;
  read: boolean;
  role?: string;
  owner?: string;
  latestActivity?: string;
}

interface SerializedNotificationMessage {
  id: string;
  message: string;
  timestamp: string; // ISO string
  type: string;
  tradeId: string;
  read: boolean;
  role?: string;
  owner?: string;
  latestActivity?: string;
}

interface NotificationStore {
  notifications: NotificationMessage[];
  unreadCount: number;
  sessionStartTimestamp: Date;
  addNotification: (
    message: string,
    type: string,
    tradeId: string,
    role?: string,
    owner?: string,
    latestActivity?: string,
  ) => void;
  removeNotification: (id: string) => void;
  markAllAsRead: () => void;
  clearAllNotifications: () => void;
  clearNotificationsByTab: (
    tab: "active" | "history",
    sessionStart: Date,
  ) => void;
  initializeFromLocalStorage: () => void;
}

// ─── Persistence Helpers ────────────────────────────────────────────────────

export function pruneNotifications(
  notifications: NotificationMessage[],
): NotificationMessage[] {
  const cutoff = new Date(Date.now() - MAX_AGE_MS);
  const nonExpired = notifications.filter((n) => n.timestamp > cutoff);
  return nonExpired.slice(0, MAX_NOTIFICATIONS);
}

export function hydrateNotifications(
  serialized: SerializedNotificationMessage[],
): NotificationMessage[] {
  return serialized.map((n) => ({
    ...n,
    timestamp: new Date(n.timestamp),
  }));
}

function saveToLocalStorage(notifications: NotificationMessage[]): void {
  try {
    localStorage.setItem(
      NOTIFICATION_STORAGE_KEY,
      JSON.stringify(notifications),
    );
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

// ─── Store ──────────────────────────────────────────────────────────────────

const useNotificationStore = create<NotificationStore>((set) => ({
  notifications: [],
  unreadCount: 0,
  sessionStartTimestamp: new Date(),

  addNotification: (message, type, tradeId, role?, owner?, latestActivity?) =>
    set((state) => {
      const newNotification: NotificationMessage = {
        id: `${Date.now()}-${Math.random()}`,
        message,
        timestamp: new Date(),
        type,
        tradeId,
        read: false,
        role,
        owner,
        latestActivity,
      };

      const updatedNotifications = pruneNotifications([
        newNotification,
        ...state.notifications,
      ]);
      const unreadCount = updatedNotifications.filter((n) => !n.read).length;

      saveToLocalStorage(updatedNotifications);

      return {
        notifications: updatedNotifications,
        unreadCount,
      };
    }),

  removeNotification: (id) =>
    set((state) => {
      const notificationToRemove = state.notifications.find(
        (n) => n.id === id,
      );
      const updatedNotifications = state.notifications.filter(
        (n) => n.id !== id,
      );
      const wasUnread = notificationToRemove && !notificationToRemove.read;

      saveToLocalStorage(updatedNotifications);

      return {
        notifications: updatedNotifications,
        unreadCount: wasUnread
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount,
      };
    }),

  markAllAsRead: () =>
    set((state) => {
      const updatedNotifications = state.notifications.map((n) => ({
        ...n,
        read: true,
      }));
      saveToLocalStorage(updatedNotifications);
      return {
        notifications: updatedNotifications,
        unreadCount: 0,
      };
    }),

  clearAllNotifications: () => {
    saveToLocalStorage([]);
    set({
      notifications: [],
      unreadCount: 0,
    });
  },

  clearNotificationsByTab: (tab, sessionStart) =>
    set((state) => {
      const remaining = state.notifications.filter((n) =>
        tab === "active"
          ? n.timestamp < sessionStart
          : n.timestamp >= sessionStart,
      );
      const unreadCount = remaining.filter((n) => !n.read).length;
      saveToLocalStorage(remaining);
      return { notifications: remaining, unreadCount };
    }),

  initializeFromLocalStorage: () => {
    const saved = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved) as SerializedNotificationMessage[];
      const hydrated = hydrateNotifications(parsed);
      const pruned = pruneNotifications(hydrated);

      // Persist the pruned list (time/count eviction only — never delete based on blotter)
      saveToLocalStorage(pruned);

      // For display, filter to only trades still on the blotter
      const tradeData = tradeDataStore.getState().tradeData;
      const tradeIds = new Set(tradeData.map((t: any) => t.esid));
      const visible = tradeIds.size > 0
        ? pruned.filter((n) => !n.tradeId || tradeIds.has(n.tradeId))
        : pruned; // If blotter is empty/not loaded yet, show all

      const unreadCount = visible.filter((n) => !n.read).length;

      set({ notifications: visible, unreadCount });
    } catch {
      // Corrupted data — start fresh
    }
  },
}));

export default useNotificationStore;
